import React, { useState } from "react";
import PropTypes from "prop-types";
import { Grid, GridItem } from "@domestika-dev/prudencio/dist/atoms/Layout";
import { Form } from "@domestika-dev/prudencio/dist/atoms/Form";
import { PlusCircle, CloseHeavy } from "@domestika-dev/prudencio/dist/atoms/Icons";
import { useIntl, FormattedMessage } from "react-intl";

import { URL, CLIENT, CLIENT_URL, AGENCY, AGENCY_URL } from "../config";

export const ExtraDataArea = ({
  project,
  handleChange,
  onFocusElement,
  focusedElement,
  reference,
  ...rest
}) => {
  const intl = useIntl();

  const [hasUrl, setHasUrl] = useState(project.url || false);
  const [hasClientInfo, setHasClientInfo] = useState(project.client || project.clientUrl || false);
  const [hasAgencyInfo, setHasAgencyInfo] = useState(project.agency || project.agencyUrl || false);

  const {
    url: urlErrors = [],
    client: clientErrors = [],
    client_url: clientUrlErrors = [],
    agency: agencyErrors = [],
    agency_url: agencyUrlErrors = [],
  } = project.errorMessage;

  const MenuContentItemOptions = [
    {
      id: "url",
      label: intl.formatMessage({ id: "projects.new.more_info_section.button.url" }),
      icon: hasUrl ? <CloseHeavy /> : <PlusCircle />,
      action: () => {
        setHasUrl(!hasUrl);

        if (hasUrl) {
          handleChange({ url: "" });
        }
      },
      disabled: false,
    },
    {
      id: "clientInfo",
      label: intl.formatMessage({ id: "projects.new.more_info_section.button.client" }),
      icon: hasClientInfo ? <CloseHeavy /> : <PlusCircle />,
      action: () => {
        setHasClientInfo(!hasClientInfo);

        if (hasClientInfo) {
          handleChange({ client: "", clientUrl: "" });
        }
      },
      disabled: false,
    },
    {
      id: "agencyInfo",
      label: intl.formatMessage({ id: "projects.new.more_info_section.button.agency" }),
      icon: hasAgencyInfo ? <CloseHeavy /> : <PlusCircle />,
      action: () => {
        setHasAgencyInfo(!hasAgencyInfo);

        if (hasAgencyInfo) {
          handleChange({ agency: "", agencyUrl: "" });
        }
      },
      disabled: false,
    },
  ];

  return (
    <>
      <div data-label="project-extra-data-menu">
        <Form.Field
          size="small"
          label={intl.formatMessage({ id: "projects.new.more_info_section.title" })}
          id="ExtraDataMenu"
          {...rest}
        >
          <Form.MenuContentItem options={MenuContentItemOptions} />
        </Form.Field>
      </div>

      <div data-label="project-extra-data">
        <Grid gridTemplateAreas={'"url" "client" "clientUrl" "agency" "agencyUrl"'}>
          {hasUrl && (
            <GridItem
              gridArea="url"
              marginTop={[4, 4, 5]}
              {...{ "data-label": "project-url-field" }}
              ref={focusedElement === URL ? reference : null}
            >
              <Form.Field
                size="small"
                label={intl.formatMessage({ id: "projects.new.url.input.title" })}
                id="url"
              >
                <Form.Input
                  size="medium"
                  id="url"
                  type="url"
                  name="url"
                  maxLength={255}
                  defaultValue={project?.url}
                  hasError={urlErrors.length > 0}
                  placeholderMessage={intl.formatMessage({
                    id: "projects.new.url.input.placeholder",
                  })}
                  onChange={e => handleChange({ url: e.target.value })}
                  onFocus={() => onFocusElement({ id: URL })}
                />
              </Form.Field>

              {urlErrors.length > 0 && <Form.Error>{urlErrors.join(", ")}</Form.Error>}
            </GridItem>
          )}

          {hasClientInfo && (
            <>
              <GridItem
                gridArea="client"
                marginTop={[4, 4, 5]}
                {...{ "data-label": "project-client-field" }}
                ref={focusedElement === CLIENT ? reference : null}
              >
                <Form.Field
                  size="small"
                  label={intl.formatMessage({ id: "projects.new.client.input.title" })}
                  id="client"
                >
                  <Form.Input
                    size="medium"
                    id="client"
                    name="client"
                    placeholderMessage={intl.formatMessage({
                      id: "projects.new.client.input.placeholder",
                    })}
                    maxLength={255}
                    defaultValue={project?.client}
                    hasError={clientErrors.length > 0}
                    onChange={e => handleChange({ client: e.target.value })}
                    onFocus={() => onFocusElement({ id: CLIENT })}
                  />
                </Form.Field>

                {clientErrors.length > 0 && <Form.Error>{clientErrors.join(", ")}</Form.Error>}
              </GridItem>

              <GridItem
                gridArea="clientUrl"
                marginTop={[4, 4, 5]}
                {...{ "data-label": "project-client-url-field" }}
                ref={focusedElement === CLIENT_URL ? reference : null}
              >
                <Form.Field
                  size="small"
                  label={intl.formatMessage({ id: "projects.new.client_url.input.title" })}
                  id="clientUrl"
                >
                  <Form.Input
                    size="medium"
                    id="clientUrl"
                    name="clientUrl"
                    type="url"
                    maxLength={255}
                    defaultValue={project?.clientUrl}
                    hasError={clientUrlErrors.length > 0}
                    placeholderMessage={intl.formatMessage({
                      id: "projects.new.client_url.input.placeholder",
                    })}
                    onChange={e => handleChange({ clientUrl: e.target.value })}
                    onFocus={() => onFocusElement({ id: CLIENT_URL })}
                  />
                </Form.Field>

                {clientUrlErrors.length > 0 && (
                  <Form.Error>{clientUrlErrors.join(", ")}</Form.Error>
                )}
              </GridItem>
            </>
          )}

          {hasAgencyInfo && (
            <>
              <GridItem
                gridArea="agency"
                marginTop={[4, 4, 5]}
                {...{ "data-label": "project-agency-field" }}
                ref={focusedElement === AGENCY ? reference : null}
              >
                <Form.Field
                  size="small"
                  label={intl.formatMessage({ id: "projects.new.agency.input.title" })}
                  id="agency"
                >
                  <Form.Input
                    size="medium"
                    id="agency"
                    name="agency"
                    maxLength={255}
                    placeholderMessage={intl.formatMessage({
                      id: "projects.new.agency.input.placeholder",
                    })}
                    defaultValue={project?.agency}
                    hasError={agencyErrors.length > 0}
                    onChange={e => handleChange({ agency: e.target.value })}
                    onFocus={() => onFocusElement({ id: AGENCY })}
                  />
                </Form.Field>

                {agencyErrors.length > 0 && <Form.Error>{agencyErrors.join(", ")}</Form.Error>}
              </GridItem>

              <GridItem
                gridArea="agencyUrl"
                marginTop={[4, 4, 5]}
                {...{ "data-label": "project-agency-url-field" }}
                ref={focusedElement === AGENCY_URL ? reference : null}
              >
                <Form.Field
                  size="small"
                  label={intl.formatMessage({ id: "projects.new.agency_url.input.title" })}
                  id="agencyUrl"
                >
                  <Form.Input
                    size="medium"
                    id="agencyUrl"
                    name="agencyUrl"
                    type="url"
                    placeholderMessage={intl.formatMessage({
                      id: "projects.new.agency_url.input.placeholder",
                    })}
                    maxLength={255}
                    defaultValue={project?.agencyUrl}
                    hasError={agencyUrlErrors.length > 0}
                    onChange={e => handleChange({ agencyUrl: e.target.value })}
                    onFocus={() => onFocusElement({ id: AGENCY_URL })}
                  />
                </Form.Field>

                {agencyUrlErrors.length > 0 && (
                  <Form.Error>{agencyUrlErrors.join(", ")}</Form.Error>
                )}
              </GridItem>
            </>
          )}
        </Grid>
      </div>
    </>
  );
};

ExtraDataArea.propTypes = {
  project: PropTypes.object,
  handleChange: PropTypes.func,
  onFocusElement: PropTypes.func,
  focusedElement: PropTypes.object,
  reference: PropTypes.func,
};

ExtraDataArea.defaultProps = {
  project: {},
  handleChange: () => {},
  onFocusElement: () => {},
  focusedElement: {},
  reference: () => {},
};

export default ExtraDataArea;
